// src/features/admin/AziendePiani.jsx
import React, { useEffect, useState } from "react";
import { Building2, Loader2, Search } from "lucide-react";

const PIANI = ["FREE", "SYNCRO", "IOT_GUARDIAN", "CUBO_PREDICTIVE"];

const PIANI_LABELS = {
  FREE: "Norma Free",
  SYNCRO: "Norma Start",
  IOT_GUARDIAN: "Norma Pro",
  CUBO_PREDICTIVE: "Norma Enterprise",
};

export default function AziendePiani({ isSuper }) {
  const [loading, setLoading] = useState(true);
  const [aziende, setAziende] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [errore, setErrore] = useState("");
  const [ricerca, setRicerca] = useState("");

  const query = ricerca.trim().toLowerCase();
  const aziendeFiltrate = aziende.filter((az) => {
    if (!query) return true;
    if (/^\d+$/.test(query)) return String(az.id) === String(Number(query));
    return String(az.nome || "").toLowerCase().includes(query);
  });

  useEffect(() => {
    if (!isSuper) return;

    async function load() {
      setLoading(true);
      setErrore("");

      try {
        const res = await fetch("/api/admin/aziende-plans-list.php", {
          credentials: "include",
        });

        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error("Risposta non valida dal server (lista aziende).");
        }

        if (!res.ok || !data.ok) {
          throw new Error(data.message || "Errore caricamento aziende.");
        }

        setAziende(Array.isArray(data.aziende) ? data.aziende : []);
      } catch (err) {
        setErrore(err.message);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [isSuper]);

  const salvaPiano = async (aziendaId, planCode) => {
    setSavingId(aziendaId);
    setErrore("");

    try {
      const res = await fetch("/api/admin/aziende-plans-save.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ azienda_id: aziendaId, plan_code: planCode }),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Risposta non valida dal server (salvataggio piano).");
      }

      if (!res.ok || !data.ok) {
        throw new Error(data.message || "Errore salvataggio piano.");
      }

      // aggiorno lo stato locale così il select resta coerente
      setAziende((prev) =>
        prev.map((az) =>
          az.id === aziendaId ? { ...az, plan_code: planCode } : az
        )
      );
    } catch (err) {
      setErrore(err.message);
      alert("Errore: " + err.message);
    } finally {
      setSavingId(null);
    }
  };

  if (!isSuper) {
    return (
      <div className="p-3 text-sm text-slate-600">
        Solo il superadmin può modificare i piani delle aziende.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 flex items-center gap-2 text-slate-600">
        <Loader2 size={16} className="animate-spin" /> Caricamento aziende…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Building2 size={18} />
          Gestione Piani Aziende
        </h2>
        <p className="text-sm text-slate-500">
          Imposta il piano (Norma Free / Norma Start / Norma Pro / Norma Enterprise) per ogni azienda.
        </p>
      </div>

      {errore && (
        <div className="bg-amber-100 text-amber-900 px-3 py-2 rounded text-sm">
          {errore}
        </div>
      )}

      <div className="rounded-xl border bg-white p-3 space-y-2">
        <label htmlFor="ricerca-aziende-piani" className="block text-sm font-medium text-slate-700">
          Cerca azienda per ID o nome
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search size={16} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              id="ricerca-aziende-piani"
              type="search"
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
              placeholder="Es. 123 oppure nome azienda"
              className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>
          {ricerca && (
            <button
              type="button"
              onClick={() => setRicerca("")}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              Azzera filtro
            </button>
          )}
        </div>
        <p role="status" className="text-xs text-slate-500">
          Aziende visualizzate: {aziendeFiltrate.length} di {aziende.length}
        </p>
      </div>

      <div className="grid gap-3">
        {aziendeFiltrate.length === 0 && (
          <div className="rounded-xl border bg-white p-4 text-sm text-slate-500">
            {query ? "Nessuna azienda trovata. Prova un altro ID o nome." : "Nessuna azienda presente."}
          </div>
        )}
        {aziendeFiltrate.map((az) => (
          <div
            key={az.id}
            className="bg-white border rounded-xl p-3 shadow-sm flex flex-col md:flex-row md:items-center gap-3 md:justify-between"
          >
            <div>
              <div className="font-semibold">{az.nome}</div>
              <div className="text-xs text-slate-500">
                ID {az.id} – {az.indirizzo || "Indirizzo non inserito"}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <select
                className="border rounded-lg px-2 py-1 text-sm"
                value={az.plan_code || "FREE"}
                onChange={(e) => salvaPiano(az.id, e.target.value)}
                disabled={savingId === az.id}
              >
                {PIANI.map((p) => (
                  <option key={p} value={p}>
                    {PIANI_LABELS[p] || p}
                  </option>
                ))}
              </select>

              {savingId === az.id && (
                <Loader2 size={18} className="animate-spin text-slate-600" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
